import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './widget-preview.module.scss';

interface QuickQuestionsProps {
  questions: string[];
  scrollLeftAriaLabel: string;
  scrollRightAriaLabel: string;
  onSelect: (question: string) => void;
}

export const QuickQuestions = ({
  questions,
  scrollLeftAriaLabel,
  scrollRightAriaLabel,
  onSelect,
}: QuickQuestionsProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const firstRow = questions.filter((_, index) => index % 2 === 0);
  const secondRow = questions.filter((_, index) => index % 2 === 1);

  const updateScrollState = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    setCanScrollLeft(scroller.scrollLeft > 1);
    setCanScrollRight(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const scroller = scrollerRef.current;
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateScrollState);
    if (scroller) observer.observe(scroller);
    return () => observer.disconnect();
  }, [questions, updateScrollState]);

  if (questions.length === 0) return null;

  const scroll = (direction: -1 | 1) => {
    scrollerRef.current?.scrollBy({ left: direction * 180, behavior: 'smooth' });
  };

  const renderRow = (items: string[]) => (
    <div className={styles.quickRow}>
      {items.map((question, index) => (
        <button key={`${question}-${index}`} type="button" onClick={() => onSelect(question)}>
          {question}
        </button>
      ))}
    </div>
  );

  return (
    <div className={styles.quickQuestions}>
      {canScrollLeft && (
        <button
          type="button"
          className={`${styles.scrollButton} ${styles.scrollLeft}`}
          onClick={() => scroll(-1)}
          aria-label={scrollLeftAriaLabel}
        >
          ‹
        </button>
      )}
      <div ref={scrollerRef} className={styles.quickScroller} onScroll={updateScrollState}>
        {renderRow(firstRow)}
        {secondRow.length > 0 && renderRow(secondRow)}
      </div>
      {canScrollRight && (
        <button
          type="button"
          className={`${styles.scrollButton} ${styles.scrollRight}`}
          onClick={() => scroll(1)}
          aria-label={scrollRightAriaLabel}
        >
          ›
        </button>
      )}
    </div>
  );
};
